import React, { useEffect, useState } from "react";
import { Eye, EyeOff } from "lucide-react";
import { toast } from "sonner";
import { api } from "../../lib/api";

/**
 * ActiveStatusToggle — small switch on /profile that flips the public
 * "active now" green dot ON or OFF. Persists via PUT /me/privacy.
 *
 * Default is ON (show active status) — users only have to come here if
 * they want to go dark.
 */
export default function ActiveStatusToggle() {
  const [show, setShow] = useState(true);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  // Initial fetch — the privacy endpoint returns the current value on PUT
  // with empty body, which keeps us from needing a separate GET.
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { data } = await api.put("/me/privacy", {});
        if (alive) setShow(data.show_active_status !== false);
      } catch {
        if (alive) setShow(true);
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, []);

  const flip = async () => {
    if (busy) return;
    const next = !show;
    setBusy(true);
    try {
      const { data } = await api.put("/me/privacy", { show_active_status: next });
      setShow(data.show_active_status !== false);
      toast.success(next ? "Active status visible" : "Active status hidden");
    } catch {
      toast.error("Couldn't save");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      data-testid="active-status-toggle"
      className="flex items-center justify-between gap-4 p-3 border border-white/10 bg-[#050505]"
    >
      <div className="min-w-0">
        <div className="font-mono text-[10px] tracking-[0.25em] uppercase text-zinc-300 flex items-center gap-1.5">
          {show ? <Eye size={11} className="text-emerald-400" /> : <EyeOff size={11} className="text-zinc-500" />}
          Active-status dot
        </div>
        <p className="text-xs text-zinc-500 mt-1">
          {show
            ? "Visitors see a green dot when you've been active in the last 5 minutes."
            : "You're invisible — no dot on your profile no matter when you're online."}
        </p>
      </div>
      <button
        type="button"
        role="switch"
        aria-checked={show}
        onClick={flip}
        disabled={loading || busy}
        data-testid="active-status-switch"
        className={`relative shrink-0 w-12 h-6 border transition-colors ${
          show ? "border-emerald-400 bg-emerald-400/20" : "border-white/15 bg-zinc-900"
        } disabled:opacity-50`}
      >
        <span
          className={`absolute top-[2px] w-[18px] h-[18px] transition-all ${
            show ? "left-[24px] bg-emerald-400" : "left-[2px] bg-zinc-500"
          }`}
          style={show ? { boxShadow: "0 0 12px rgba(34,197,94,0.6)" } : undefined}
        />
      </button>
    </div>
  );
}
